<template>
  <div
    class="view-pipeline-page"
    :class="{ 'view-pipeline-page--table': viewMode === 'table' }"
  >
    <template v-if="viewMode === 'board'">
      <v-card flat class="view-pipeline-card d-flex flex-column">
        <v-card-text class="view-pipeline-card-text">
          <div class="view-pipeline-card-text__header d-flex align-center mb-4">
            <div class="text-h5 flex-grow-1">
              {{ pipelineName || "-" }}
            </div>
            <v-btn
              v-if="hasPermissions('pipelines-edit')"
              color="primary"
              class="mr-3"
              :to="{ name: 'pipelines-edit', params: { id: pipelineId } }"
            >
              {{ $t("GENERAL.LABELS.EDIT_BUTTON") }}
            </v-btn>
            <v-btn
              v-if="canDelete"
              color="error"
              class="mr-3"
              :disabled="isProcessing"
              @click="deleteConfirm = true"
            >
              {{ $t("GENERAL.LABELS.DELETE") }}
            </v-btn>
            <v-btn-toggle
              v-model="viewMode"
              mandatory
              dense
              class="view-pipeline-view-toggle"
            >
              <v-btn value="board" small>
                <v-icon small left>mdi-view-column</v-icon>
                {{ $t("PIPELINES.VIEW_BOARD") }}
              </v-btn>
              <v-btn value="table" small>
                <v-icon small left>mdi-table</v-icon>
                {{ $t("PIPELINES.VIEW_TABLE") }}
              </v-btn>
            </v-btn-toggle>
          </div>

          <PipelineBoard
            :pipeline-id="pipelineId"
            @pipeline-loaded="onPipelineLoaded"
          />
        </v-card-text>
      </v-card>
    </template>

    <template v-else>
      <div class="view-pipeline-card-text__header d-flex align-center mb-4">
        <div class="text-h5 flex-grow-1">
          {{ pipelineName || "-" }}
        </div>
        <v-btn
          v-if="hasPermissions('pipelines-edit')"
          color="primary"
          class="mr-3"
          :to="{ name: 'pipelines-edit', params: { id: pipelineId } }"
        >
          {{ $t("GENERAL.LABELS.EDIT_BUTTON") }}
        </v-btn>
        <v-btn
          v-if="canDelete"
          color="error"
          class="mr-3"
          :disabled="isProcessing"
          @click="deleteConfirm = true"
        >
          {{ $t("GENERAL.LABELS.DELETE") }}
        </v-btn>
        <v-btn-toggle
          v-model="viewMode"
          mandatory
          dense
          class="view-pipeline-view-toggle"
        >
          <v-btn value="board" small>
            <v-icon small left>mdi-view-column</v-icon>
            {{ $t("PIPELINES.VIEW_BOARD") }}
          </v-btn>
          <v-btn value="table" small>
            <v-icon small left>mdi-table</v-icon>
            {{ $t("PIPELINES.VIEW_TABLE") }}
          </v-btn>
        </v-btn-toggle>
      </div>

      <DealsTable
        :pipeline-id="pipelineId"
        :title="pipelineName"
        @pipeline-loaded="onPipelineLoaded"
      />
    </template>
    <Confirm
      v-model="deleteConfirm"
      :content="$t('GENERAL.DELETE_CONFIRM')"
      @confirm="deletePipeline"
      @decline="deleteConfirm = false"
    />
  </div>
</template>

<script>
import PipelineBoard from "@/views/pages/pipelines/partials/PipelineBoard";
import DealsTable from "@/views/partials/deals/DealsTable";
import Confirm from "@/views/partials/dialogs/Confirm.vue";
import { DELETE_PIPELINE } from "@/store/pipelines.module";
import { SHOW_SNACK } from "@/store/snack.module";

export default {
  name: "ViewPipeline",
  components: {
    PipelineBoard,
    DealsTable,
    Confirm,
  },
  data() {
    return {
      pipelineName: "",
      viewMode: "board",
      isProcessing: false,
      deleteConfirm: false,
    };
  },
  computed: {
    pipelineId() {
      return this.$route.params.id;
    },
    canDelete() {
      return this.hasPermissions("pipelines-delete") && this.pipelineId;
    },
  },
  mounted() {
    if (!this.hasPermissions("pipelines-view-all") || !this.hasPermissions(["deals-view-all", "deals-view-self"])) {
      this.$router.push({ name: "dashboard" });
      return;
    }

    this.viewMode = "board";
  },
  watch: {
    pipelineId() {
      this.pipelineName = "";
      this.viewMode = "board";
    },
  },
  methods: {
    onPipelineLoaded({ name }) {
      this.pipelineName = name || "";
    },
    getErrorMessage(error) {
      if (error && error.errors && typeof error.errors === "object") {
        const firstErrorField = Object.keys(error.errors)[0];
        const firstErrorList = error.errors[firstErrorField];

        if (Array.isArray(firstErrorList) && firstErrorList.length) {
          return firstErrorList[0];
        }
      }

      if (error && error.message) {
        return error.message;
      }

      return this.$t("GENERAL.UNEXPECTED_ERROR");
    },
    deletePipeline() {
      this.isProcessing = true;
      this.$store
        .dispatch(DELETE_PIPELINE, this.pipelineId)
        .then(() => {
          this.$store.dispatch(SHOW_SNACK, {
            message: this.$t("GENERAL.SUCCESS_MESSAGE"),
          });
          this.$router.push({ name: "pipelines" });
        })
        .catch((error) => {
          this.$store.dispatch(SHOW_SNACK, {
            type: "error",
            message: this.getErrorMessage(error),
          });
        })
        .finally(() => {
          this.isProcessing = false;
          this.deleteConfirm = false;
        });
    },
  },
};
</script>

<style scoped>
.view-pipeline-page {
  margin: -25px;
  width: calc(100% + 50px);
  max-width: none;
  height: calc(100vh - 65px);
  min-height: calc(100vh - 65px);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-top: 2px solid rgba(0, 0, 0, 0.12);
  background-color: #fff;
}

.view-pipeline-page--table {
  margin: 0;
  width: 100%;
  max-width: 100%;
  height: auto;
  min-height: 0;
  overflow: visible;
  border-top: none;
  background-color: transparent;
}

.view-pipeline-card {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background-color: #fff !important;
}

.view-pipeline-card-text {
  flex: 1 1 auto;
  min-height: 0;
  display: flex !important;
  flex-direction: column;
  overflow: hidden;
  background-color: #fff !important;
}

.view-pipeline-card-text__header {
  flex-shrink: 0;
}

.view-pipeline-view-toggle {
  flex-shrink: 0;
}

@media (max-width: 1023px) {
  .view-pipeline-page:not(.view-pipeline-page--table) {
    margin: -15px;
    width: calc(100% + 30px);
    height: calc(100vh - 56px);
    min-height: calc(100vh - 56px);
  }
}
</style>
